import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Icon } from '@iconify/react';
import fileFill from '@iconify/icons-eva/file-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { BsFillCloudDownloadFill } from 'react-icons/bs';
import { AnimatePresence, motion } from 'framer-motion';
import { isString } from 'lodash';
import { cn } from 'src/@/lib/utils';
import { Button } from 'src/@/components/ui/button';
import { fData } from 'src/utils/formatNumber';

export default function ChamadosArquivos({
  error,
  showPreview = false,
  files,
  filesEdit,
  completado,
  onRemoveEdit,
  onRemove,
  isEditFile,
  className,
  ...other
}) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    ...other,
    multiple: true,
    maxSize: 5000000,
  });

  const ShowRejectionItems = () => (
    <div className="py-2 px-4 mt-3 rounded-md bg-red-50 border border-red-200">
      {fileRejections.map(({ file, errors }) => {
        const { path, size } = file;
        return (
          <div key={path} className="my-1">
            <p className="text-sm font-medium text-red-800 break-all">
              {path} - {size ? fData(size) : ''}
            </p>
            {errors.map((e) => (
              <p key={e.code} className="text-xs text-red-600">
                - Arquivo muito grande. Máximo permitido: <strong>5mb</strong>
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={cn("w-full", className)}>
      {!completado && (
        <div
          {...getRootProps()}
          className={cn(
            "outline-none flex flex-col items-center justify-center py-6 px-4 rounded-xl transition-all cursor-pointer border-dashed border-2",
            isDragActive ? "opacity-70 ring-2 ring-[#7C3AED] bg-[#7C3AED]/5" : "bg-gray-50 border-gray-300 hover:bg-gray-100",
            (isDragReject || error) && "bg-red-50 border-red-300"
          )}
        >
          <input {...getInputProps()} />

          <div className="flex flex-col items-center text-center gap-2">
            <div className="p-3 rounded-full bg-blue-50 text-blue-600 mb-1">
              <Icon icon={fileFill} className="w-6 h-6" />
            </div>
            <p className="text-sm text-gray-600">
              Arraste arquivos ou selecione <span className="text-[#7C3AED] underline font-medium">buscando</span> em sua máquina
            </p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Limite máximo de 5mb</p>
          </div>
        </div>
      )}

      {fileRejections.length > 0 && <ShowRejectionItems />}

      {/* Files Edit List (Arquivos já existentes) */}
      {isEditFile && filesEdit?.length > 0 && (
        <div className="mt-4 flex flex-col gap-2">
          <AnimatePresence>
            {filesEdit.map((file) => {
              const { key, name, size } = file;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500">
                    <Icon icon={fileFill} width={24} height={24} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate" title={name}>{name}</p>
                    <p className="text-xs text-gray-400">{fData(size)}</p>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-[#7C3AED] hover:bg-[#7C3AED]/10"
                      onClick={() => window.open(`https://lais-app.s3.us-east-2.amazonaws.com/${key}`, '_blank')}
                    >
                      <BsFillCloudDownloadFill size={16} />
                    </Button>
                    {!completado && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => onRemoveEdit(file)}
                      >
                        <Icon icon={closeFill} width={18} height={18} />
                      </Button>
                    )}
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      )}

      {/* New Files List (Arquivos novos sendo submetidos) */}
      {files?.length > 0 && (
        <div className="mt-4 flex flex-col gap-2">
          <AnimatePresence>
            {files.map((file) => {
              const { name, size } = file;
              const key = isString(file) ? file : name;

              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white shadow-sm"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500">
                    <Icon icon={fileFill} width={24} height={24} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{isString(file) ? file : name}</p>
                    <p className="text-xs text-gray-400">{isString(file) ? '' : fData(size)}</p>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                    onClick={() => onRemove(file)}
                  >
                    <Icon icon={closeFill} width={18} height={18} />
                  </Button>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
